import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  useProcurementMyPurchaseRequestsDatatable,
} from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { contactFullName } from 'modules/finance/lib/contactsUtils'
import { PurchaseRequestDeadline } from 'modules/procurement/components/purchaseRequests/Deadline'
import { EmptyState } from 'modules/procurement/components/purchaseRequests/EmptyState'
import { SpendingRequestStatusLabel } from 'modules/procurement/components/purchaseRequests/SpendingRequestStatusLabel'

interface Props {}

const MyPurchaseRequestsDataTable = ({}: Props) => {
  const {
    employee,
    access: { locale },
    company,
  } = useCurrentSession()
  const { navigation } = useNavigation()

  const source = useProcurementMyPurchaseRequestsDatatable(
    [
      {
        identifier: 'description',
        title: i18n.t('procurement.purchase_requests.list.description'),
        kind: ProjectionKind.Custom,
        template: '{{ description }}',
        render: ({ description }) => {
          return (
            <Box minWidth="s120">
              <Text.Normal size="200" weight="medium">
                {description}
              </Text.Normal>
            </Box>
          )
        },
        sortBy: 'description',
      },
      {
        identifier: 'status',
        title: i18n.t('procurement.purchase_requests.list.status'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ status }}',
        render: ({ status }) => {
          return <SpendingRequestStatusLabel status={status} />
        },
        sortBy: 'status',
      },
      {
        identifier: 'amount',
        kind: ProjectionKind.Custom,
        title: i18n.t('procurement.purchase_requests.list.cost'),
        template: '{{ amount }} {{ currency }}',
        textAlign: 'right',
        justifyContent: 'flexEnd',
        render: ({ cost }) => {
          return (
            <Box alignItems="flexEnd">
              {cost && (
                <Text.Normal size="200" weight="medium">
                  {MoneyWithCurrency({
                    amount: Number(cost.cents),
                    currency: cost.currency,
                    locale,
                  })}
                </Text.Normal>
              )}
            </Box>
          )
        },
      },
      {
        identifier: 'date',
        title: i18n.t('procurement.purchase_requests.list.requested'),
        kind: ProjectionKind.Custom,
        template: '{{ date }}',
        render: ({ date }) => {
          return <Text.Normal size="200">{dateBackendDateTimeToUI(date)}</Text.Normal>
        },
        sortBy: 'date',
      },
      {
        identifier: 'deadline',
        title: i18n.t('procurement.purchase_requests.list.deadline'),
        kind: ProjectionKind.Custom,
        template: '{{ deadline }}',
        render: ({ deadline, status }) => {
          return (
            <Box minWidth="s64">
              <PurchaseRequestDeadline deadline={deadline} status={status} />
            </Box>
          )
        },
        sortBy: 'deadline',
        colSpan: 1,
      },
      {
        identifier: 'vendor',
        title: i18n.t('procurement.purchase_requests.list.vendor'),
        kind: ProjectionKind.Custom,
        template: '{{ vendor.legalName }}',
        render: ({ vendor }) => {
          return <Text.Normal size="200">{contactFullName(vendor)}</Text.Normal>
        },
      },
      {
        identifier: 'detail',
        kind: ProjectionKind.Decorative,
        render: ({ id, status }) => (
          <Button.Router
            type="secondary"
            to={navigation.myPurchases.detail.fullPath({ id: id })}
            iconName="arrow-right"
            onClick={() =>
              track(trackingEvents.procurement.viewPurchaseRequestDetail, {
                page: 'My Purchase Requests',
                status: status,
              })
            }
          />
        ),
      },
    ],
    {
      variables: { companyId: company.id, requesterEmployeeIds: [employee.id] },
    }
  )

  return (
    <DataTable.Provider
      source={source}
      pagination={{
        kind: 'offset',
        itemsPerPage: 25,
      }}
      sort={{
        path: 'date',
        direction: 'desc',
      }}
    >
      <Box>
        <DataTable.HeadBar
          disableDefaultActions
          actions={
            <Box paddingY="s8">
              <Button.Router
                iconName="add"
                type="primary"
                to={navigation.myPurchases.newRequest.fullPath()}
                onClick={() => track(trackingEvents.procurement.clickNewPurchaseRequest)}
              >
                {i18n.t('procurement.purchase_requests.actions.new_request')}
              </Button.Router>
            </Box>
          }
        />
        <DataTable.Table
          autoWidth
          emptyState={
            <EmptyState
              description={i18n.t('procurement.purchase_requests.empty.description_employee')}
            />
          }
        />
      </Box>
    </DataTable.Provider>
  )
}

export default MyPurchaseRequestsDataTable
